import { EffectWrapper } from "core/Materials/effectRenderer.js";
import { DecorateSymbol, DisableUniform, GetShaderCreateOptions } from "../utils/shaderCodeUtils.js";
import { CreateStrongRef } from "./strongRef.js";
/**
 * The shader bindings for a ShaderBlock that can't be disabled.
 */
export class ShaderBinding {
    constructor() {
        this._remappedShaderVariables = {};
    }
    /**
     * Gets the remapped shader variable name.
     * @param variableName - The variable name
     * @returns The remapped variable name
     */
    getRemappedName(variableName) {
        variableName = DecorateSymbol(variableName);
        return this._remappedShaderVariables[variableName] ?? variableName;
    }
    /**
     * Sets the remapped shader variables.
     * @param variableName - defines the variable name to remap
     * @param remappedName - defines the remapped name
     */
    addShaderVariableRemapping(variableName, remappedName) {
        this._remappedShaderVariables[variableName] = remappedName;
    }
}
/**
 * The shader bindings for a disableable ShaderBlock.
 */
export class DisableableShaderBinding extends ShaderBinding {
    /**
     * Construct a ShaderBinding instance.
     * @param parentBlock - The parent block
     */
    constructor(parentBlock) {
        super();
        this._disabled = parentBlock.disabled?.runtimeData || CreateStrongRef(false);
    }
    /**
     * Binds all the required data to the shader when rendering.
     * @param effect - defines the effect to bind the data to
     * @param _width - defines the width of the output
     * @param _height - defines the height of the output
     */
    bind(effect, _width, _height) {
        effect.setBool(this.getRemappedName(DisableUniform), this._disabled.value);
    }
}
/**
 * The shader runtime is the base for any runtime associated with a @see ShaderBlock.
 *
 * It encapsulates the basic needs to render a full screen effect mainly the effect wrapper holding on the shader program.
 *
 * It is able to either render to a texture or directly to the main canvas.
 *
 * It also manages the disposal of the effect wrapper.
 */
export class ShaderRuntime {
    /**
     * Creates a new @see ShaderRuntime.
     * @param effectRenderer - defines the effect renderer to use to render the full screen effect
     * @param shaderProgram - defines the shader code associated with this runtime
     * @param shaderBinding - defines the shader bindings associated with this runtime
     */
    constructor(effectRenderer, shaderProgram, shaderBinding) {
        this._engine = effectRenderer.engine;
        this._effectRenderer = effectRenderer;
        this._shaderBinding = shaderBinding;
        this._effectWrapper = new EffectWrapper({
            engine: this._engine,
            ...GetShaderCreateOptions(shaderProgram),
        });
        // Wraps the effect readiness in a promise to expose it as a public property.
        this.onReadyAsync = new Promise((resolve, reject) => {
            this._effectWrapper.effect.executeWhenCompiled(() => {
                resolve();
            });
            this._effectWrapper.effect.onErrorObservable.addOnce((error) => {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(error);
            });
        });
    }
    /**
     * Renders the full screen effect into a render target.
     * @param outputBlock - The output block to render to - assumes it has a .renderTargetWrapper
     */
    renderToTargetWrapper(outputBlock) {
        this._renderToTargetWrapper(outputBlock.renderTargetWrapper);
    }
    /**
     * Renders the full screen effect into a texture.
     * @param renderTargetTexture - The render target texture to render into
     */
    renderToTargetTexture(renderTargetTexture) {
        const renderTargetWrapper = renderTargetTexture.renderTarget;
        if (renderTargetWrapper) {
            this._renderToTargetWrapper(renderTargetWrapper);
        }
    }
    _renderToTargetWrapper(renderTargetWrapper) {
        this._engine.bindFramebuffer(renderTargetWrapper);
        this._draw(renderTargetWrapper.width, renderTargetWrapper.height);
        this._engine.unBindFramebuffer(renderTargetWrapper);
    }
    /**
     * Renders the full screen effect into the main canvas.
     */
    renderToCanvas() {
        this._effectRenderer.setViewport();
        this._draw(this._engine.getRenderWidth(), this._engine.getRenderHeight());
    }
    /**
     * "Draws" the full screen effect into the currently bound output.
     * @param width - defines the width of the output
     * @param height - defines the height of the output
     */
    _draw(width, height) {
        this._effectRenderer.applyEffectWrapper(this._effectWrapper);
        this._shaderBinding.bind(this._effectWrapper.effect, width, height);
        this._effectRenderer.draw();
    }
    /**
     * Disposes the runtime resources.
     */
    dispose() {
        this._effectWrapper.dispose();
    }
}
//# sourceMappingURL=shaderRuntime.js.map